import * as React from 'react';
import { loadingMessages } from '@federa/utils/styles/content-tokens';
import { colors } from '../css-refs.colors.ts';
import { typography } from '../css-refs.typography.ts';
import { spacing } from '../css-refs.spacing.ts';
import { borderRadius } from '../css-refs.effects.ts';
import { ARIA_ROLES } from '../common/aria-tokens.ts';
import spinnerStyles from './Spinner.module.css';
import type { SpinnerProps, SpinnerSize, SpinnerVariant } from '@federa/types/ui-spinner';
const LOADING_TEXT = loadingMessages.default;

const VARIANT_COLORS: Record<SpinnerVariant, string> = {
	primary: colors.brand.primary,
	secondary: colors.text.secondary,
};

const SIZE_STYLES: Record<SpinnerSize, { size: string; borderWidth: string; fontSize: string }> = {
	xs: {
		size: spacing[3],
		borderWidth: spacing[0.5],
		fontSize: typography.fontSize.xs,
	},
	sm: {
		size: spacing[4],
		borderWidth: spacing[0.5],
		fontSize: typography.fontSize.sm,
	},
	md: {
		size: spacing[6],
		borderWidth: spacing[0.75],
		fontSize: typography.fontSize.sm,
	},
	lg: {
		size: spacing[8],
		borderWidth: spacing[1],
		fontSize: typography.fontSize.base,
	},
	xl: {
		size: spacing[12],
		borderWidth: spacing[1.25],
		fontSize: typography.fontSize.base,
	},
};

export const Spinner = React.forwardRef<HTMLDivElement, SpinnerProps>(
	({ size = 'md', variant = 'primary', label = LOADING_TEXT, showLabel = false, className, style, ...props }, ref) => {
		const currentSize = SIZE_STYLES[size];
		const color = VARIANT_COLORS[variant];

		const containerStyles: React.CSSProperties = {
			display: 'inline-flex',
			flexDirection: 'column',
			alignItems: 'center',
			gap: spacing.sm,
			...style,
		};

		const spinnerCss: React.CSSProperties = {
			width: currentSize.size,
			height: currentSize.size,
			border: `${currentSize.borderWidth} solid ${color}`,
			borderTopColor: 'transparent',
			borderRadius: borderRadius.full,
		};

		const labelStyles: React.CSSProperties = {
			fontSize: currentSize.fontSize,
			color: colors.text.secondary,
		};

		return (
			<div ref={ref} style={containerStyles} className={className} {...props}>
				<div className={spinnerStyles.spinner} style={spinnerCss} role={ARIA_ROLES.status} aria-label={label} />
				{showLabel && <span style={labelStyles}>{label}</span>}
			</div>
		);
	},
);

Spinner.displayName = 'Spinner';
